/*************************************************************************
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { loadStatus } from "constants/enums";
import { createURLFromBuffer } from "dc-preview-utils/src/Utils/UrlUtils";
import docApi from "api";

class AcroformAppearance {
  constructor(fieldId) {
    this.fieldId = fieldId;
    this.appearanceMap = {}; // contains down,normal,rollover appearance
  }

  getAppearance(pageIndex, data) {
    const { appearanceType, appearanceState } = data;
    const appearanceImageUrlKey = `${appearanceType}_${appearanceState}`;
    if (!this.appearanceMap[appearanceImageUrlKey]) {
      this.appearanceMap[appearanceImageUrlKey] = {
        url: "",
        status: loadStatus.NOT_LOADED,
        promise: null
      };
    }
    if (this.appearanceMap[appearanceImageUrlKey].status === loadStatus.NOT_LOADED) {
      return this.loadAppearance(pageIndex, data);
    } else if (this.appearanceMap[appearanceImageUrlKey].status === loadStatus.BEING_LOADED) {
      return this.appearanceMap[appearanceImageUrlKey].promise;
    }
    return Promise.resolve(this.appearanceMap[appearanceImageUrlKey].url);
  }

  revokeObjectURL(url) {
    url && window.URL.revokeObjectURL(url);
  }

  setAppearance(appearanceImageUrlKey, url, status) {
    this.revokeObjectURL(this.appearanceMap[appearanceImageUrlKey].url);
    this.appearanceMap[appearanceImageUrlKey].url = url;
    this.appearanceMap[appearanceImageUrlKey].status = status;
  }

  setAppearanceStatus(appearanceImageUrlKey, status) {
    this.appearanceMap[appearanceImageUrlKey].status = status;
  }

  processAppearanceResponse(appearanceType, appearanceState, response) {
    let imgUrl = "";
    if (response && response.data && response.data.foundAppearance) {
      imgUrl = createURLFromBuffer(response.data.buffer, response.data.mimeType);
    }
    const appearanceImageUrlKey = `${appearanceType}_${appearanceState}`;
    this.setAppearance(appearanceImageUrlKey, imgUrl, loadStatus.LOADED);
  }

  loadAppearance(pageIndex, data) {
    const { appearanceType, appearanceState } = data;
    const appearanceImageUrlKey = `${appearanceType}_${appearanceState}`;
    this.appearanceMap[appearanceImageUrlKey].status = loadStatus.BEING_LOADED;

    const promise = docApi
      .getCommentAppearance(pageIndex, this.fieldId, data)
      .then(response => {
        this.processAppearanceResponse(appearanceType, appearanceState, response);
        return this.appearanceMap[appearanceImageUrlKey].url;
      })
      .catch(error => {
        this.appearanceMap[appearanceImageUrlKey].status = loadStatus.NOT_LOADED;
        return "";
      })
      .finally(() => {
        this.appearanceMap[appearanceImageUrlKey].promise = null;
      });

    this.appearanceMap[appearanceImageUrlKey].promise = promise;
    return promise;
  }

  unloadAppearances(shouldUnloadAppearnce = () => true) {
    for (const [key, subItem] of Object.entries(this.appearanceMap)) {
      if (shouldUnloadAppearnce(subItem)) {
        this.setAppearanceStatus(key, loadStatus.NOT_LOADED);
      }
    }
  }

  revokeAppearances() {
    for (const [key] of Object.entries(this.appearanceMap)) {
      this.setAppearance(key, "", loadStatus.NOT_LOADED);
    }
  }
}

export { AcroformAppearance };
